<script setup lang="ts">
import { ECharts } from 'echarts'
import type { Values } from '@/composables/useValues'

defineProps<{
    dimension: Dimension
    country: Country
    data: Values
}>()

/**
 * Translations.
 */
const { t } = useI18n()

/**
 * Use params.
 */
const { config } = useParams()

/**
 * Milestones chart instance.
 */
const milestonesInstance = shallowRef<ECharts>()

/**
 * Activities chart instance.
 */
const activitiesInstance = shallowRef<ECharts>()

/**
 * Indicators chart instance.
 */
const indicatorsInstance = shallowRef<ECharts>()

/**
 * Create a computed property for the country card ID.
 */
function createComputedCountryCardId(
    index: number,
    id = config.countryCard?.[0]
) {
    return computed({
        get() {
            return config.countryCard?.[index]
        },
        set(value) {
            if (value === undefined) return

            if (!config.countryCard) {
                config.countryCard = [String(id), String(value)]
            } else {
                const values = config.countryCard
                    .slice(1, index)
                    .concat(String(value))

                config.countryCard = [String(id), ...values]
            }
        }
    })
}

/**
 * Selected milestone ID.
 */
const milestoneId = createComputedCountryCardId(1, 'activities')

/**
 * Selected activity ID.
 */
const activityId = createComputedCountryCardId(2, 'indicators')
</script>

<template>
    <!-- <CountryCard
        id="progress"
        v-slot="{ expanded }"
        :dimension="dimension"
        :country="country"
        expanded="row-span-6 col-start-3 col-span-5"
        condensed="row-span-2 col-span-2 !aspect-auto"
        class="col-span-2 row-span-1 !aspect-auto"
        :title="t('indicators-progress')"
    >
        <CountryGovernmentResponseIndicatorsProgress
            :dimension="dimension"
            :country="country"
            :expanded="expanded"
        />
    </CountryCard> -->

    <CountryCard
        id="milestones"
        v-slot="{ condensed }"
        :instance="milestonesInstance"
        :dimension="dimension"
        :country="country"
        :loading="data.loading"
        expanded="row-span-6 col-start-3 col-span-5 max-sm:!aspect-[1/2]"
        condensed="row-span-3 col-span-2 max-sm:!aspect-[1/2]"
        class="col-span-2 row-span-4 max-sm:!aspect-[1/2]"
        :title="t('milestones')"
    >
        <CountryGovernmentResponseMilestones
            v-model:instance="milestonesInstance"
            v-model:milestoneId="milestoneId"
            :dimension="dimension"
            :condensed="condensed"
        />
    </CountryCard>

    <CountryCard
        id="activities"
        v-slot="{ condensed }"
        parent-id="milestones"
        :dimension="dimension"
        :country="country"
        :loading="data.loading"
        :instance="activitiesInstance"
        expanded="row-span-6 col-start-3 col-span-5 max-sm:!aspect-[2/3]"
        condensed="row-span-3 col-span-2 max-sm:!aspect-[2/3]"
        class="col-span-1 col-start-3 row-span-2 max-sm:!aspect-[2/3]"
        :title="t('activities')"
    >
        <CountryGovernmentResponseActivities
            v-model:activityId="activityId"
            v-model:instance="activitiesInstance"
            :condensed="condensed"
            :milestone-id="milestoneId"
        />
    </CountryCard>

    <CountryCard
        id="indicators"
        v-slot="{ expanded, condensed }"
        parent-id="activities"
        :dimension="dimension"
        :country="country"
        :loading="data.loading"
        :instance="indicatorsInstance"
        expanded="row-span-6 col-start-3 col-span-5 max-sm:!aspect-auto"
        condensed="row-span-3 col-span-2 max-sm:!aspect-auto"
        class="col-span-1 col-start-3 row-span-2 max-sm:!aspect-auto"
        :title="t('indicators')"
    >
        <CountryGovernmentResponseIndicators
            v-model:instance="indicatorsInstance"
            :expanded="expanded"
            :condensed="condensed"
            :activity-id="activityId"
        />
    </CountryCard>
</template>